// Dwellow heat pump cost simulator: public embeddable widget.
//
// Runs the shared @dwellowtech/heat-pump-cost-engine entirely in the browser,
// so results update instantly and every assumption can be shown. The widget
// injects its own markup and styles into the mount element, so the Webflow
// snippet stays tiny: one div plus the Alpine CDN and this module.
//
// Mount: <div data-journey="heat-pump-cost-simulator" data-quote-url="..."></div>

import {
    DEFAULTS_2026,
    MONTHLY_PROFILE_PRESETS,
    boilerEfficiencyForAge,
    simulate,
    spaceScopForFlowTemp,
} from '@dwellowtech/heat-pump-cost-engine';

const DEFAULT_QUOTE_URL = 'https://www.dwellow.co.uk/heat-pumps/quote';

const TYPICAL_HP_RATE = DEFAULTS_2026.tariff.electricityPencePerKwhByPreset.cosy;
const TYPICAL_HP_OFFPEAK = DEFAULTS_2026.tariff.offPeakPencePerKwhByPreset.cosy;
const FLAT_CAP_RATE = DEFAULTS_2026.tariff.electricityPencePerKwhByPreset.flat_cap;

const FUEL_OPTIONS = [
    { value: 'mains_gas', label: 'Gas' },
    { value: 'oil', label: 'Oil' },
    { value: 'lpg', label: 'LPG' },
    { value: 'direct_electric', label: 'Electric' },
    { value: 'storage_electric', label: 'Storage heaters' },
];

const UNIT_PRICE_META = {
    mains_gas: { min: 4, max: 14, defaultRate: DEFAULTS_2026.currentPrices.unitPricePencePerKwhByFuel.mains_gas, source: 'Ofgem cap, July to September 2026' },
    oil: { min: 4, max: 16, defaultRate: DEFAULTS_2026.currentPrices.unitPricePencePerKwhByFuel.oil, source: 'typical delivered price, July 2026', perUnitLabel: 'p/litre', kwhPerUnit: 10.35 },
    lpg: { min: 6, max: 18, defaultRate: DEFAULTS_2026.currentPrices.unitPricePencePerKwhByFuel.lpg, source: 'typical bulk LPG', perUnitLabel: 'p/litre', kwhPerUnit: 7.11 },
    direct_electric: { min: 15, max: 40, defaultRate: DEFAULTS_2026.currentPrices.unitPricePencePerKwhByFuel.direct_electric, source: 'Ofgem cap, July to September 2026' },
    storage_electric: { min: 10, max: 35, defaultRate: DEFAULTS_2026.currentPrices.unitPricePencePerKwhByFuel.storage_electric, source: 'typical Economy 7 blend' },
};

const STYLES = `
.hpcs { --ink: #1c2b33; --muted: #5b6b73; --line: #dde4e7; --card: #ffffff; --bg: #f5f7f7;
  --hp: #0f766e; --hp-soft: #6cc0b6; --cur: #c26a2a; --cur-soft: #e6b489;
  --warn-bg: #fef3cd; --warn-ink: #6b5514; --good: #0f766e;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--ink); max-width: 760px; margin: 0 auto; font-size: 16px; line-height: 1.45; }
.hpcs *, .hpcs *::before, .hpcs *::after { box-sizing: border-box; }
.hpcs[x-cloak], .hpcs [x-cloak] { display: none !important; }
.hpcs-card { background: var(--card); border: 1px solid var(--line); border-radius: 14px; padding: 18px; margin-bottom: 14px; }
.hpcs-card h3 { margin: 0 0 4px; font-size: 17px; }
.hpcs-sub { margin: 0 0 12px; color: var(--muted); font-size: 14px; }
.hpcs-headline { display: grid; gap: 10px; grid-template-columns: 1fr; }
@media (min-width: 560px) { .hpcs-headline { grid-template-columns: 1.2fr 1fr 1fr; } }
.hpcs-tile { border: 1px solid var(--line); border-radius: 12px; padding: 14px; background: var(--bg); }
.hpcs-tile.is-primary { background: var(--hp); border-color: var(--hp); color: #fff; }
.hpcs-tile.is-warning { background: var(--warn-bg); border-color: #e5cf7a; color: var(--warn-ink); }
.hpcs-tile .hpcs-tile-label { font-size: 13px; opacity: 0.85; }
.hpcs-tile .hpcs-tile-value { font-size: 26px; font-weight: 700; margin-top: 2px; font-variant-numeric: tabular-nums; }
.hpcs-tile .hpcs-tile-foot { font-size: 12px; opacity: 0.8; margin-top: 2px; }
.hpcs-meta-row { display: flex; flex-wrap: wrap; gap: 8px; justify-content: space-between; align-items: center; margin-top: 12px; font-size: 13px; color: var(--muted); }
.hpcs-seg { display: inline-flex; flex-wrap: wrap; gap: 4px; background: var(--bg); border: 1px solid var(--line); border-radius: 10px; padding: 3px; }
.hpcs-seg button { border: 0; background: transparent; padding: 7px 12px; border-radius: 8px; font: inherit; font-size: 14px; font-weight: 500; color: var(--muted); cursor: pointer; }
.hpcs-seg button.is-active { background: var(--hp); color: #fff; }
.hpcs-field { margin: 12px 0; }
.hpcs-label { display: flex; justify-content: space-between; align-items: center; gap: 10px; font-size: 14px; font-weight: 600; margin-bottom: 6px; }
.hpcs-label .hpcs-value { background: var(--bg); border: 1px solid var(--line); border-radius: 8px; padding: 3px 9px; font-weight: 600; font-size: 13px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.hpcs input[type="range"] { width: 100%; accent-color: var(--hp); height: 26px; margin: 0; }
.hpcs input[type="text"], .hpcs input[type="number"] { width: 100%; border: 1px solid var(--line); border-radius: 10px; padding: 9px 11px; font: inherit; }
.hpcs-hint { font-size: 12.5px; color: var(--muted); margin-top: 4px; }
.hpcs-hint button { border: 0; background: none; color: var(--hp); font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 0; text-decoration: underline; }
.hpcs-grid2 { display: grid; gap: 12px; grid-template-columns: 1fr; }
@media (min-width: 560px) { .hpcs-grid2 { grid-template-columns: 1fr 1fr; } }
.hpcs-grid3 { display: grid; gap: 12px; grid-template-columns: 1fr; }
@media (min-width: 560px) { .hpcs-grid3 { grid-template-columns: 1fr 1fr 1fr; } }
.hpcs-note { background: var(--bg); border: 1px solid var(--line); border-radius: 10px; padding: 12px; font-size: 13.5px; color: var(--muted); }
.hpcs-note strong { color: var(--ink); }
.hpcs-warn { background: var(--warn-bg); border: 1px solid #e5cf7a; color: var(--warn-ink); border-radius: 10px; padding: 12px; font-size: 13.5px; }
.hpcs-assume { border: 1px solid var(--line); border-radius: 10px; padding: 12px; }
.hpcs-assume p.hpcs-assume-title { margin: 0 0 8px; font-size: 11.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted); }
.hpcs-assume ul { margin: 0; padding-left: 18px; font-size: 13.5px; color: var(--muted); }
.hpcs-assume li { margin: 6px 0; }
.hpcs-assume li strong { color: var(--ink); }
.hpcs-chart { display: flex; align-items: flex-end; gap: 4px; height: 150px; margin-top: 10px; }
.hpcs-chart .hpcs-mcol { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; height: 100%; }
.hpcs-chart .hpcs-mbars { display: flex; gap: 2px; align-items: flex-end; height: 100%; }
.hpcs-chart .hpcs-bar { flex: 1; display: flex; flex-direction: column-reverse; border-radius: 3px 3px 0 0; overflow: hidden; }
.hpcs-chart .hpcs-bs { width: 100%; }
.hpcs-mlabels { display: flex; gap: 4px; margin-top: 4px; }
.hpcs-mlabels span { flex: 1; text-align: center; font-size: 10.5px; color: var(--muted); }
.hpcs-legend { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 10px; font-size: 12.5px; color: var(--muted); }
.hpcs-legend span::before { content: ""; display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin-right: 5px; vertical-align: -1px; background: var(--sw); }
.hpcs-svg { width: 100%; height: auto; margin-top: 8px; }
.hpcs-details { border: 1px solid var(--line); border-radius: 14px; background: var(--card); margin-bottom: 14px; }
.hpcs-details summary { list-style: none; cursor: pointer; padding: 16px 18px; display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.hpcs-details summary::-webkit-details-marker { display: none; }
.hpcs-details summary .hpcs-sum-title { font-weight: 600; }
.hpcs-details summary .hpcs-sum-sub { display: block; font-weight: 400; font-size: 13px; color: var(--muted); }
.hpcs-details summary .hpcs-chev { color: var(--muted); transition: transform 0.15s ease; }
.hpcs-details[open] summary .hpcs-chev { transform: rotate(180deg); }
.hpcs-details .hpcs-details-body { border-top: 1px solid var(--line); padding: 16px 18px; }
.hpcs-table-wrap { overflow-x: auto; border: 1px solid var(--line); border-radius: 10px; }
.hpcs table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.hpcs table th { text-align: left; padding: 8px 10px; background: var(--bg); font-size: 12.5px; }
.hpcs table th.hpcs-num, .hpcs table td.hpcs-num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.hpcs table td { padding: 7px 10px; border-top: 1px solid var(--line); vertical-align: top; }
.hpcs table td.hpcs-how { color: var(--muted); }
.hpcs table tr.hpcs-group td { background: var(--bg); font-size: 11.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--muted); padding: 5px 10px; }
.hpcs table tr.hpcs-total td { font-weight: 700; border-top: 2px solid var(--line); background: rgba(15, 118, 110, 0.06); }
.hpcs-cta { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; justify-content: space-between; background: var(--hp); border-radius: 14px; padding: 18px; color: #fff; margin-bottom: 14px; }
.hpcs-cta p { margin: 0; }
.hpcs-cta .hpcs-cta-sub { font-size: 13.5px; opacity: 0.85; margin-top: 2px; }
.hpcs-cta a { background: #fff; color: var(--hp); font-weight: 700; text-decoration: none; padding: 11px 18px; border-radius: 10px; font-size: 15px; white-space: nowrap; }
.hpcs-foot { font-size: 12px; color: var(--muted); margin: 6px 2px 0; }
.hpcs-stickybar { position: fixed; left: 50%; bottom: 0; z-index: 9990; width: min(760px, calc(100vw - 16px));
  background: var(--hp); color: #fff; padding: 11px 16px calc(11px + env(safe-area-inset-bottom));
  display: flex; flex-wrap: wrap; align-items: baseline; justify-content: center; gap: 2px 14px;
  font-size: 13.5px; border-radius: 12px 12px 0 0; box-shadow: 0 -4px 18px rgba(0, 0, 0, 0.18);
  transform: translate(-50%, 110%); transition: transform 0.25s ease; pointer-events: none; }
.hpcs-stickybar.is-visible { transform: translate(-50%, 0); }
.hpcs-stickybar.is-negative { background: #9a6a10; }
.hpcs-stickybar .hpcs-sb-label { opacity: 0.85; }
.hpcs-stickybar .hpcs-sb-value { font-size: 17px; font-weight: 700; font-variant-numeric: tabular-nums; }
.hpcs-stickybar .hpcs-sb-detail { opacity: 0.85; font-variant-numeric: tabular-nums; }
.hpcs-check { display: flex; gap: 9px; align-items: flex-start; border: 1px solid var(--line); border-radius: 10px; padding: 11px; cursor: pointer; font-size: 14px; }
.hpcs-check input { accent-color: var(--hp); margin-top: 3px; }
.hpcs-check .hpcs-check-hint { display: block; font-size: 12.5px; color: var(--muted); }
`;

const TEMPLATE = `
<div class="hpcs" x-cloak>
  <div class="hpcs-card hpcs-hero">
    <div class="hpcs-headline">
      <div class="hpcs-tile" :class="r.year1.savingGbp >= 0 ? 'is-primary' : 'is-warning'">
        <div class="hpcs-tile-label" x-text="r.year1.savingGbp >= 0 ? ('Estimated saving ' + periodLabel) : ('Extra cost ' + periodLabel)"></div>
        <div class="hpcs-tile-value" x-text="money(Math.abs(r.year1.savingGbp) / periodDivisor)"></div>
        <div class="hpcs-tile-foot" x-text="money(Math.abs(r.totals.lifetimeSavingGbp)) + (r.totals.lifetimeSavingGbp >= 0 ? ' over 10 years' : ' extra over 10 years')"></div>
      </div>
      <div class="hpcs-tile">
        <div class="hpcs-tile-label" x-text="'Current fuel ' + periodLabel"></div>
        <div class="hpcs-tile-value" style="color: var(--cur)" x-text="money(r.year1.currentCostGbp / periodDivisor)"></div>
      </div>
      <div class="hpcs-tile">
        <div class="hpcs-tile-label" x-text="'Heat pump ' + periodLabel"></div>
        <div class="hpcs-tile-value" style="color: var(--hp)" x-text="money(r.year1.heatPumpCostGbp / periodDivisor)"></div>
      </div>
    </div>
    <div class="hpcs-meta-row">
      <span x-text="r.totals.lifetimeCarbonTonnes.toFixed(1) + ' tonnes of CO2 avoided over 10 years'"></span>
      <span class="hpcs-seg">
        <button type="button" :class="{ 'is-active': period === 'year' }" @click="period = 'year'">Per year</button>
        <button type="button" :class="{ 'is-active': period === 'month' }" @click="period = 'month'">Per month</button>
      </span>
    </div>
  </div>

  <div class="hpcs-warn" x-show="r.year1.savingGbp < 0" style="margin-bottom: 14px;">
    On these figures a heat pump costs more to run than your current system. The biggest levers are the
    electricity tariff (a heat pump tariff helps most), the flow temperature (lower is more efficient),
    and removing the gas standing charge.
  </div>

  <div class="hpcs-card">
    <h3>Your current heating</h3>
    <p class="hpcs-sub">Tell us the fuel and either the usage or just the bill.</p>
    <div class="hpcs-field">
      <div class="hpcs-label">Fuel</div>
      <div class="hpcs-seg">
        <template x-for="opt in fuelOptions" :key="opt.value">
          <button type="button" :class="{ 'is-active': fuel === opt.value }" @click="setFuel(opt.value)" x-text="opt.label"></button>
        </template>
      </div>
    </div>
    <div class="hpcs-grid2">
      <div class="hpcs-field">
        <div class="hpcs-label">How do you want to tell us?</div>
        <div class="hpcs-seg">
          <template x-for="opt in unitOptions" :key="opt.value">
            <button type="button" :class="{ 'is-active': unit === opt.value }" @click="setUnit(opt.value)" x-text="opt.label"></button>
          </template>
        </div>
      </div>
      <div class="hpcs-field">
        <div class="hpcs-label"><span x-text="amountLabel"></span><span class="hpcs-value" x-text="amountBounds.suffix"></span></div>
        <input type="number" inputmode="numeric" :min="amountBounds.min" :max="amountBounds.max" x-model.number="amount">
      </div>
    </div>
    <div class="hpcs-field">
      <div class="hpcs-label">Your fuel unit price <span class="hpcs-value" x-text="unitPriceDisplay"></span></div>
      <input type="range" :min="priceMeta.min" :max="priceMeta.max" step="0.05" x-model.number="unitPrice">
      <p class="hpcs-hint">
        <span x-text="'Default ' + priceMeta.defaultRate.toFixed(2) + ' p/kWh (' + priceMeta.source + ').' + (unit === 'bill' ? ' Also used to work out your usage from the bill.' : '')"></span>
        <button type="button" x-show="Math.abs(unitPrice - priceMeta.defaultRate) > 0.01" @click="unitPrice = priceMeta.defaultRate">Reset</button>
      </p>
    </div>
  </div>

  <div class="hpcs-card">
    <h3>Heat pump and tariff</h3>
    <div class="hpcs-field">
      <div class="hpcs-label">Design flow temperature <span class="hpcs-value" x-text="flowTempC + ' C, SPF ' + spaceScop.toFixed(2)"></span></div>
      <input type="range" min="35" max="55" step="5" x-model.number="flowTempC" @input="spaceScop = round2(scopForFlow(flowTempC))">
      <p class="hpcs-hint">Lower flow temperatures run more efficiently. Your installer confirms this at design.</p>
    </div>
    <div class="hpcs-grid2">
      <div class="hpcs-field">
        <div class="hpcs-label">Electricity tariff</div>
        <div class="hpcs-seg">
          <button type="button" :class="{ 'is-active': tariffKey === 'typical' }" @click="setTariff('typical')">Heat pump tariff</button>
          <button type="button" :class="{ 'is-active': tariffKey === 'cap' }" @click="setTariff('cap')">Price cap</button>
          <button type="button" :class="{ 'is-active': tariffKey === 'custom' }" @click="setTariff('custom')">Custom</button>
        </div>
      </div>
      <div class="hpcs-field">
        <div class="hpcs-label">Electricity unit rate <span class="hpcs-value" x-text="tariffRate.toFixed(1) + ' p/kWh'"></span></div>
        <input type="range" min="5" max="40" step="0.5" x-model.number="tariffRate">
      </div>
    </div>
    <div class="hpcs-grid2" x-show="tariffKey === 'custom'">
      <label class="hpcs-check">
        <input type="checkbox" :checked="offPeakRate != null" @change="offPeakRate = $event.target.checked ? 9 : null">
        <span>Tariff has a cheap off peak window
          <span class="hpcs-check-hint">A battery can only cut costs by charging in a cheap window.</span>
        </span>
      </label>
      <div class="hpcs-field" x-show="offPeakRate != null">
        <div class="hpcs-label">Off peak window rate <span class="hpcs-value" x-text="(offPeakRate ?? 0).toFixed(1) + ' p/kWh'"></span></div>
        <input type="range" min="2" :max="Math.max(3, tariffRate - 1)" step="0.5" x-model.number="offPeakRate">
      </div>
    </div>
    <p class="hpcs-hint" x-text="tariffHint"></p>
  </div>

  <div class="hpcs-card">
    <h3>Hot water</h3>
    <p class="hpcs-sub">Hot water is a big share of heat pump running cost. Set it by household size or by cylinder.</p>
    <div class="hpcs-seg" style="margin-bottom: 12px;">
      <button type="button" :class="{ 'is-active': hwMethod === 'occupancy' }" @click="hwMethod = 'occupancy'">By household</button>
      <button type="button" :class="{ 'is-active': hwMethod === 'cylinder' }" @click="hwMethod = 'cylinder'">By cylinder</button>
    </div>
    <div class="hpcs-field" x-show="hwMethod === 'occupancy'">
      <div class="hpcs-label">People in the home <span class="hpcs-value" x-text="occupants + (occupants === 1 ? ' person' : ' people')"></span></div>
      <input type="range" min="1" max="6" step="1" x-model.number="occupants">
    </div>
    <div class="hpcs-grid3" x-show="hwMethod === 'cylinder'">
      <div class="hpcs-field">
        <div class="hpcs-label">Cylinder size <span class="hpcs-value" x-text="cylinderLitres + ' L'"></span></div>
        <input type="range" min="100" max="400" step="10" x-model.number="cylinderLitres">
      </div>
      <div class="hpcs-field">
        <div class="hpcs-label">Store temperature <span class="hpcs-value" x-text="cylinderTargetC + ' C'"></span></div>
        <input type="range" min="45" max="65" step="1" x-model.number="cylinderTargetC">
      </div>
      <div class="hpcs-field">
        <div class="hpcs-label">Reheats per day <span class="hpcs-value" x-text="reheatsPerDay.toFixed(1) + 'x'"></span></div>
        <input type="range" min="0.5" max="3" step="0.5" x-model.number="reheatsPerDay">
      </div>
    </div>
    <p class="hpcs-hint" x-text="'Hot water demand: ' + kwhFmt(r.assumptions.hotWaterDemandKwh) + ' (' + Math.round((r.assumptions.hotWaterDemandKwh / Math.max(r.assumptions.usefulHeatKwh, 1)) * 100) + '% of your heat).'"></p>
  </div>

  <div class="hpcs-card">
    <h3>Solar and battery</h3>
    <p class="hpcs-sub">Optional. Add them to see their honest contribution and exactly what we assume.</p>
    <div class="hpcs-grid2">
      <div class="hpcs-field">
        <div class="hpcs-label">Solar PV size <span class="hpcs-value" x-text="solarKwp === 0 ? 'None' : solarKwp.toFixed(1) + ' kWp'"></span></div>
        <input type="range" min="0" max="15" step="0.5" x-model.number="solarKwp">
        <div class="hpcs-field" x-show="solarKwp > 0" style="margin-bottom: 0;">
          <div class="hpcs-label" style="font-weight: 500; font-size: 13px;">Roof orientation</div>
          <div class="hpcs-seg">
            <button type="button" :class="{ 'is-active': orientation === 'south' }" @click="orientation = 'south'">South</button>
            <button type="button" :class="{ 'is-active': orientation === 'east_west' }" @click="orientation = 'east_west'">East / west</button>
            <button type="button" :class="{ 'is-active': orientation === 'other' }" @click="orientation = 'other'">Other</button>
          </div>
        </div>
      </div>
      <div class="hpcs-field">
        <div class="hpcs-label">Home battery <span class="hpcs-value" x-text="batteryKwh === 0 ? 'None' : batteryKwh.toFixed(1) + ' kWh'"></span></div>
        <input type="range" min="0" max="30" step="0.5" x-model.number="batteryKwh">
      </div>
    </div>
    <div class="hpcs-warn" x-show="batteryKwh > 0 && r.assumptions.batteryOffPeakPencePerKwh == null && solarKwp === 0">
      On this tariff the battery has nothing to work with: there is no cheap off peak window to charge from,
      and no solar to store. Add solar, pick the heat pump tariff, or give your custom tariff an off peak rate.
    </div>
    <div class="hpcs-assume" x-show="solarKwp > 0 || r.assumptions.batterySavingGbp > 0 || (batteryKwh > 0 && r.assumptions.batteryOffPeakPencePerKwh != null)" style="margin-top: 12px;">
      <p class="hpcs-assume-title">What we are assuming</p>
      <ul>
        <li x-show="solarKwp > 0">
          <strong>Solar generation:</strong>
          <span x-text="solarKwp.toFixed(1) + ' kWp x ' + (r.assumptions.solarYieldKwhPerKwp ?? 0) + ' kWh per kWp = ' + kwhFmt(r.assumptions.solarAnnualGenerationKwh) + ', mostly in summer.'"></span>
        </li>
        <li x-show="solarKwp > 0">
          <strong>Share reaching the heat pump:</strong>
          <span x-text="Math.round((r.assumptions.solarSelfConsumptionFraction ?? 0) * 100) + '% of generation, capped month by month at what the heat pump actually needs. Winter solar is small and summer solar mostly covers hot water.'"></span>
        </li>
        <li x-show="solarKwp > 0">
          <strong>Panels alone:</strong>
          <span x-text="kwhFmt(r.assumptions.solarToHeatPumpKwh - r.assumptions.batterySolarStorageKwh) + ' of heat pump electricity covered, worth ' + money(r.assumptions.solarSavingGbp) + ' a year.'"></span>
        </li>
        <li x-show="batteryKwh > 0 && r.assumptions.batterySolarStorageKwh > 0">
          <strong>Battery storing your solar:</strong>
          <span x-text="'holds midday sun for morning and evening heating, capturing an extra ' + kwhFmt(r.assumptions.batterySolarStorageKwh) + ', worth ' + money(r.assumptions.batterySolarSavingGbp) + ' a year. This works on any tariff.'"></span>
        </li>
        <li x-show="batteryKwh > 0 && r.assumptions.batteryOffPeakPencePerKwh != null">
          <strong>Battery shifting cheap electricity:</strong>
          <span x-text="batteryKwh.toFixed(1) + ' kWh x ' + dodPct + '% usable x ' + cyclesPerDay + ' cycle a day = ' + r.assumptions.batteryDeliverableKwhPerDay.toFixed(1) + ' kWh a day moved into the ' + (r.assumptions.batteryOffPeakPencePerKwh ?? 0).toFixed(1) + 'p window. Shifted energy really costs ' + (r.assumptions.batteryShiftedRatePencePerKwh ?? 0).toFixed(1) + 'p per kWh once charging losses are counted, saving ' + money(r.assumptions.batteryArbitrageSavingGbp) + ' a year on top.'"></span>
        </li>
        <li x-show="batteryKwh > 0 && r.assumptions.batteryOffPeakPencePerKwh == null && r.assumptions.batterySolarStorageKwh > 0">
          <strong>No cheap window on this tariff:</strong>
          the battery only helps here by storing solar. On a heat pump tariff it would also shift cheap electricity into heating hours.
        </li>
        <li x-show="batteryKwh > 0">
          <strong>Battery total:</strong>
          <span x-text="money(r.assumptions.batterySavingGbp) + ' a year.'"></span>
        </li>
      </ul>
    </div>

    <details class="hpcs-details" x-show="solarKwp > 0 || batteryKwh > 0" style="margin-top: 12px; margin-bottom: 0;">
      <summary style="padding: 12px 14px;">
        <span class="hpcs-sum-title" style="font-size: 14px;">Month by month: what we expect</span>
        <span class="hpcs-chev">&#9662;</span>
      </summary>
      <div class="hpcs-details-body" style="padding: 0;">
        <div class="hpcs-table-wrap" style="border: 0; border-radius: 0;">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th class="hpcs-num" x-show="solarKwp > 0">Solar generated</th>
                <th class="hpcs-num" x-show="solarKwp > 0">Heat pump uses</th>
                <th class="hpcs-num" x-show="batteryKwh > 0 && solarKwp > 0">of which stored for later</th>
                <th class="hpcs-num" x-show="batteryKwh > 0 && r.assumptions.batteryShiftedKwh > 0.5">Shifted to the cheap window</th>
              </tr>
            </thead>
            <tbody>
              <template x-for="(m, idx) in r.months" :key="'sb' + idx">
                <tr>
                  <td x-text="m.month.slice(0, 3)"></td>
                  <td class="hpcs-num" x-show="solarKwp > 0" x-text="Math.round(m.solarGenerationKwh) + ' kWh'"></td>
                  <td class="hpcs-num" x-show="solarKwp > 0" x-text="Math.round(m.solarToHeatPumpKwh) + ' kWh'"></td>
                  <td class="hpcs-num" x-show="batteryKwh > 0 && solarKwp > 0" x-text="Math.round(m.batterySolarStorageKwh) + ' kWh'"></td>
                  <td class="hpcs-num" x-show="batteryKwh > 0 && r.assumptions.batteryShiftedKwh > 0.5" x-text="Math.round(m.batteryShiftedKwh) + ' kWh'"></td>
                </tr>
              </template>
              <tr class="hpcs-total">
                <td>Year</td>
                <td class="hpcs-num" x-show="solarKwp > 0" x-text="Math.round(r.assumptions.solarAnnualGenerationKwh) + ' kWh'"></td>
                <td class="hpcs-num" x-show="solarKwp > 0" x-text="Math.round(r.assumptions.solarToHeatPumpKwh) + ' kWh'"></td>
                <td class="hpcs-num" x-show="batteryKwh > 0 && solarKwp > 0" x-text="Math.round(r.assumptions.batterySolarStorageKwh) + ' kWh'"></td>
                <td class="hpcs-num" x-show="batteryKwh > 0 && r.assumptions.batteryShiftedKwh > 0.5" x-text="Math.round(r.assumptions.batteryShiftedKwh) + ' kWh'"></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="hpcs-hint" style="padding: 10px 14px; margin: 0;">
          Generation follows the UK solar season; the heat pump can only use what it needs that month, which is why
          summer generation mostly covers hot water. Stored for later means midday sun the battery holds for morning
          and evening heating. Shifting means cheap window electricity replacing your standard rate.
        </p>
      </div>
    </details>
  </div>

  <div class="hpcs-card">
    <h3>Month by month, split by use</h3>
    <p class="hpcs-sub">Running cost each month, split into heating and hot water. Heat pumps look worst in the coldest months.</p>
    <div class="hpcs-chart">
      <template x-for="(m, idx) in monthlyBars" :key="idx">
        <div class="hpcs-mcol">
          <div class="hpcs-mbars">
            <div class="hpcs-bar" :style="'height:' + m.curH + '%'">
              <div class="hpcs-bs" :style="'background: var(--cur); height:' + m.curSpacePct + '%'"></div>
              <div class="hpcs-bs" :style="'background: var(--cur-soft); height:' + m.curWaterPct + '%'"></div>
            </div>
            <div class="hpcs-bar" :style="'height:' + m.hpH + '%'">
              <div class="hpcs-bs" :style="'background: var(--hp); height:' + m.hpSpacePct + '%'"></div>
              <div class="hpcs-bs" :style="'background: var(--hp-soft); height:' + m.hpWaterPct + '%'"></div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="hpcs-mlabels">
      <template x-for="(m, idx) in monthlyBars" :key="'l' + idx"><span x-text="m.label"></span></template>
    </div>
    <div class="hpcs-legend">
      <span style="--sw: var(--cur)">Current heating</span>
      <span style="--sw: var(--cur-soft)">Current hot water</span>
      <span style="--sw: var(--hp)">Heat pump heating</span>
      <span style="--sw: var(--hp-soft)">Heat pump hot water</span>
    </div>

    <details class="hpcs-details" style="margin-top: 12px; margin-bottom: 0;">
      <summary style="padding: 12px 14px;">
        <span class="hpcs-sum-title" style="font-size: 14px;">Month by month: the numbers</span>
        <span class="hpcs-chev">&#9662;</span>
      </summary>
      <div class="hpcs-details-body" style="padding: 0;">
        <div class="hpcs-table-wrap hpcs-cop-months" style="border: 0; border-radius: 0;">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th class="hpcs-num">Heating COP</th>
                <th class="hpcs-num">Hot water COP</th>
                <th class="hpcs-num">Heat pump electricity</th>
                <th class="hpcs-num">Heat pump cost</th>
              </tr>
            </thead>
            <tbody>
              <template x-for="(m, idx) in r.months" :key="'cop' + idx">
                <tr>
                  <td x-text="m.month.slice(0, 3)"></td>
                  <td class="hpcs-num" x-text="m.spaceCop.toFixed(2)"></td>
                  <td class="hpcs-num" x-text="m.hotWaterCop.toFixed(2)"></td>
                  <td class="hpcs-num" x-text="Math.round(m.heatPumpElecKwh) + ' kWh'"></td>
                  <td class="hpcs-num" x-text="money(m.heatPumpCostGbp)"></td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <p class="hpcs-hint" style="padding: 10px 14px; margin: 0;" x-text="'COP is how many units of heat each unit of electricity delivers. Heating COP is worst in the cold months and best in summer; the demand weighted average equals your SPF of ' + r.assumptions.spaceScopUsed.toFixed(2) + '. Hot water COP dips a little in winter because the mains water arrives colder.'"></p>
      </div>
    </details>
  </div>

  <div class="hpcs-card">
    <h3>Year one to year ten</h3>
    <p class="hpcs-sub" x-text="'Annual running cost with gas at ' + gasInfl + '% and electricity at ' + elecInfl + '% inflation. The gap usually widens over time.'"></p>
    <svg class="hpcs-svg" viewBox="0 0 100 44" preserveAspectRatio="none" aria-hidden="true">
      <line x1="4" y1="38" x2="98" y2="38" stroke="#dde4e7" stroke-width="0.4"></line>
      <polyline :points="tenYear.currentPoints" fill="none" stroke="var(--cur)" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round"></polyline>
      <polyline :points="tenYear.hpPoints" fill="none" stroke="var(--hp)" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round"></polyline>
    </svg>
    <div class="hpcs-mlabels" style="margin-top: 0;">
      <span>Y1</span><span></span><span></span><span></span><span>Y5</span><span></span><span></span><span></span><span></span><span>Y10</span>
    </div>
    <div class="hpcs-legend">
      <span style="--sw: var(--cur)" x-text="'Current fuel, ' + money(tenYear.currentY10) + ' in year ten'"></span>
      <span style="--sw: var(--hp)" x-text="'Heat pump, ' + money(tenYear.hpY10) + ' in year ten'"></span>
    </div>
  </div>

  <details class="hpcs-details">
    <summary>
      <span class="hpcs-sum-title">Advanced and fine tuning
        <span class="hpcs-sum-sub">Boiler age, efficiency, seasonal profile, inflation and comfort.</span>
      </span>
      <span class="hpcs-chev">&#9662;</span>
    </summary>
    <div class="hpcs-details-body">
      <div class="hpcs-note" x-show="fuel === 'direct_electric' || fuel === 'storage_electric'" style="margin-bottom: 12px;">
        Panel and storage heaters turn every unit of electricity into heat, so we assume 100% efficiency and
        there is nothing to adjust. Storage heaters' real drawback is when they use electricity, which is
        covered by your tariff, not by efficiency.
      </div>
      <div class="hpcs-grid2" x-show="fuel !== 'direct_electric' && fuel !== 'storage_electric'">
        <div class="hpcs-field">
          <div class="hpcs-label">Boiler age <span class="hpcs-value" x-text="boilerAge >= 25 ? '25+ years' : boilerAge + (boilerAge === 1 ? ' year' : ' years')"></span></div>
          <input type="range" min="0" max="25" step="1" x-model.number="boilerAge" @input="boilerEff = round2(effForAge(fuel, boilerAge))">
          <p class="hpcs-hint">Condensing boilers measured 85% in real homes when under five years old (EST in situ monitoring study), then about 1 point per year older. Boilers over 20 years are usually non condensing.</p>
        </div>
        <div class="hpcs-field">
          <div class="hpcs-label">Boiler efficiency <span class="hpcs-value" x-text="Math.round(boilerEff * 100) + '%'"></span></div>
          <input type="range" min="0.6" max="0.95" step="0.01" x-model.number="boilerEff">
        </div>
      </div>
      <div class="hpcs-field">
        <div class="hpcs-label">Seasonal usage profile</div>
        <div class="hpcs-seg">
          <template x-for="p in profiles" :key="p.key">
            <button type="button" :class="{ 'is-active': profileKey === p.key }" @click="profileKey = p.key" x-text="p.label"></button>
          </template>
        </div>
        <p class="hpcs-hint" x-text="profileDescription + ' Sets the month by month shape; the headline SPF stays the same.'"></p>
      </div>
      <div class="hpcs-grid2">
        <div class="hpcs-field">
          <div class="hpcs-label">Space heating SPF <span class="hpcs-value" x-text="spaceScop.toFixed(2)"></span></div>
          <input type="range" min="2" max="5" step="0.05" x-model.number="spaceScop">
        </div>
        <div class="hpcs-field">
          <div class="hpcs-label">Hot water SPF <span class="hpcs-value" x-text="hotWaterScop.toFixed(2)"></span></div>
          <input type="range" min="1.8" max="3.5" step="0.05" x-model.number="hotWaterScop">
        </div>
        <div class="hpcs-field">
          <div class="hpcs-label">Gas / oil inflation <span class="hpcs-value" x-text="gasInfl + '% a year'"></span></div>
          <input type="range" min="0" max="10" step="0.5" x-model.number="gasInfl">
        </div>
        <div class="hpcs-field">
          <div class="hpcs-label">Electricity inflation <span class="hpcs-value" x-text="elecInfl + '% a year'"></span></div>
          <input type="range" min="0" max="10" step="0.5" x-model.number="elecInfl">
        </div>
      </div>
      <div class="hpcs-field">
        <div class="hpcs-label">Heat the home warmer with the heat pump <span class="hpcs-value" x-text="comfortUpliftC === 0 ? 'Same as today' : '+' + comfortUpliftC.toFixed(1) + ' C warmer'"></span></div>
        <input type="range" min="0" max="3" step="0.5" x-model.number="comfortUpliftC">
        <p class="hpcs-hint">Many heat pump owners keep their homes warmer than before. Each extra degree adds about 8% to the heat pump's heating demand. Your current bill stays as entered.</p>
      </div>
      <label class="hpcs-check" x-show="fuel === 'mains_gas'">
        <input type="checkbox" x-model="removeGas">
        <span>Remove the gas standing charge
          <span class="hpcs-check-hint" x-text="'If you disconnect from gas entirely, you save about ' + money(gasStandingSaving) + ' a year.'"></span>
        </span>
      </label>
    </div>
  </details>

  <details class="hpcs-details">
    <summary>
      <span class="hpcs-sum-title">Show the maths
        <span class="hpcs-sum-sub">Every step of the calculation, in the order it happens.</span>
      </span>
      <span class="hpcs-chev">&#9662;</span>
    </summary>
    <div class="hpcs-details-body">
      <div class="hpcs-table-wrap hpcs-maths">
        <table>
          <thead>
            <tr><th>Quantity</th><th class="hpcs-num">Value</th><th>How it is worked out</th></tr>
          </thead>
          <tbody>
            <template x-for="(row, i) in displayRows" :key="i">
              <tr :class="{ 'hpcs-group': row.type === 'group', 'hpcs-total': row.total === true }">
                <template x-if="row.type === 'group'">
                  <td colspan="3" x-text="row.label"></td>
                </template>
                <template x-if="row.type !== 'group'">
                  <td x-text="row.label"></td>
                </template>
                <template x-if="row.type !== 'group'">
                  <td class="hpcs-num" x-text="row.value"></td>
                </template>
                <template x-if="row.type !== 'group'">
                  <td class="hpcs-how" x-text="row.how"></td>
                </template>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p class="hpcs-hint" style="margin-top: 8px;">
        Prices as of the July 2026 Ofgem cap and DESNZ 2025 carbon factors. A guide, not an MCS design or a quote.
      </p>
    </div>
  </details>

  <div class="hpcs-cta">
    <p>Like these numbers?
      <span class="hpcs-cta-sub">Get a free, no pressure quote designed for your home by our team.</span>
    </p>
    <a :href="quoteUrl">Get your free quote</a>
  </div>

  <p class="hpcs-foot">
    This tool helps you explore heat pump running costs using simplified assumptions and your own inputs.
    Actual performance depends on the final system design, installation and how you use your home.
    It is not a substitute for a professional heat loss assessment or MCS certified design.
  </p>

  <template x-teleport="body">
    <div class="hpcs hpcs-stickybar" :class="{ 'is-visible': stickyOn, 'is-negative': r.year1.savingGbp < 0 }" aria-hidden="true">
      <span class="hpcs-sb-label" x-text="r.year1.savingGbp >= 0 ? 'Estimated saving' : 'Extra cost'"></span>
      <strong class="hpcs-sb-value" x-text="money(Math.abs(r.year1.savingGbp)) + ' a year'"></strong>
      <span class="hpcs-sb-detail" x-text="'Current ' + money(r.year1.currentCostGbp) + ' vs heat pump ' + money(r.year1.heatPumpCostGbp) + ' a year'"></span>
    </div>
  </template>
</div>
`;

export function registerHeatPumpCostSimulator() {
    if (typeof document === 'undefined') return;

    const createStore = () => {
        const state = {
            // Current system
            fuel: 'mains_gas',
            unit: 'bill',
            amount: 1100,
            unitPrice: UNIT_PRICE_META.mains_gas.defaultRate,
            boilerAge: 5,
            boilerEff: boilerEfficiencyForAge('mains_gas', 5),

            // Heat pump + tariff
            flowTempC: 45,
            spaceScop: spaceScopForFlowTemp(45),
            tariffKey: 'typical',
            tariffRate: TYPICAL_HP_RATE,
            offPeakRate: TYPICAL_HP_OFFPEAK,

            // Hot water
            hwMethod: 'occupancy',
            occupants: 3,
            cylinderLitres: DEFAULTS_2026.hotWater.cylinderLitres,
            cylinderTargetC: DEFAULTS_2026.hotWater.cylinderTargetC,
            reheatsPerDay: DEFAULTS_2026.hotWater.reheatsPerDay,

            // Solar and battery
            solarKwp: 0,
            orientation: 'south',
            batteryKwh: 0,

            // Advanced
            hotWaterScop: DEFAULTS_2026.heatPump.hotWaterScop,
            gasInfl: DEFAULTS_2026.projection.gasInflationPct,
            elecInfl: DEFAULTS_2026.projection.electricityInflationPct,
            comfortUpliftC: 0,
            profileKey: 'typical',
            removeGas: false,

            period: 'year',
            quoteUrl: DEFAULT_QUOTE_URL,

            fuelOptions: FUEL_OPTIONS,
            profiles: MONTHLY_PROFILE_PRESETS.map(p => ({ key: p.key, label: p.label, description: p.description })),
            gasStandingSaving: DEFAULTS_2026.tariff.gasStandingChargeSavingGbp,
            dodPct: Math.round(DEFAULTS_2026.battery.depthOfDischarge * 100),
            cyclesPerDay: DEFAULTS_2026.battery.cyclesPerDay,

            _resultKey: '',
            _result: null,
            _heroAbove: false,
            _rootOnScreen: false,

            init() {
                const root = this.$root;
                // $root is the injected .hpcs div; the config attributes live
                // on the mount element around it.
                const mountEl = root?.closest('[data-journey="heat-pump-cost-simulator"]');
                const url = mountEl?.dataset?.quoteUrl;
                if (url) this.quoteUrl = url;
                this._observeSticky(root);
            },

            // The compact saving bar appears once the headline card has
            // scrolled out of view, and leaves when the widget itself does,
            // so adjusting assumptions further down always shows the impact.
            _observeSticky(root) {
                if (typeof IntersectionObserver === 'undefined' || !root) return;
                const hero = root.querySelector('.hpcs-hero');
                if (hero) {
                    new IntersectionObserver(entries => {
                        const entry = entries[entries.length - 1];
                        this._heroAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                    }).observe(hero);
                }
                new IntersectionObserver(entries => {
                    const entry = entries[entries.length - 1];
                    this._rootOnScreen = entry.isIntersecting;
                }).observe(root);
            },

            get stickyOn() {
                return this._heroAbove && this._rootOnScreen;
            },

            // ---- input handling ----
            setFuel(fuel) {
                this.fuel = fuel;
                const allowed = this.unitOptions.map(o => o.value);
                if (!allowed.includes(this.unit)) this.unit = 'bill';
                this.amount = this.defaultAmount(fuel, this.unit);
                this.unitPrice = UNIT_PRICE_META[fuel].defaultRate;
                this.boilerEff = this.round2(this.effForAge(fuel, this.boilerAge));
            },
            setUnit(unit) {
                this.unit = unit;
                this.amount = this.defaultAmount(this.fuel, unit);
            },
            setTariff(key) {
                this.tariffKey = key;
                if (key === 'typical') {
                    this.tariffRate = TYPICAL_HP_RATE;
                    this.offPeakRate = TYPICAL_HP_OFFPEAK;
                } else if (key === 'cap') {
                    this.tariffRate = FLAT_CAP_RATE;
                    this.offPeakRate = null;
                } else {
                    this.tariffRate = FLAT_CAP_RATE;
                    this.offPeakRate = null;
                }
            },
            defaultAmount(fuel, unit) {
                if (unit === 'bill') return 1100;
                const electric = fuel === 'direct_electric' || fuel === 'storage_electric';
                if (unit === 'kwh') return electric ? 8000 : 14000;
                if (fuel === 'mains_gas') return 1300;
                if (fuel === 'lpg') return 2000;
                return 1500;
            },

            get unitOptions() {
                const electric = this.fuel === 'direct_electric' || this.fuel === 'storage_electric';
                const unitLabel = this.fuel === 'mains_gas' ? 'm3' : 'Litres';
                return electric
                    ? [{ value: 'kwh', label: 'kWh' }, { value: 'bill', label: 'Bill' }]
                    : [{ value: 'kwh', label: 'kWh' }, { value: 'bill', label: 'Bill' }, { value: 'fuel_units', label: unitLabel }];
            },
            get amountLabel() {
                return this.unit === 'bill' ? 'Annual heating spend' : this.unit === 'kwh' ? 'Annual energy use' : 'Annual fuel used';
            },
            get amountBounds() {
                if (this.unit === 'bill') return { min: 100, max: 6000, suffix: 'GBP a year' };
                if (this.unit === 'kwh') return { min: 1000, max: 60000, suffix: 'kWh a year' };
                if (this.fuel === 'mains_gas') return { min: 200, max: 6000, suffix: 'm3 a year' };
                return { min: 200, max: 8000, suffix: 'litres a year' };
            },
            get priceMeta() {
                return UNIT_PRICE_META[this.fuel];
            },
            get unitPriceDisplay() {
                const meta = this.priceMeta;
                if (meta.kwhPerUnit) {
                    return this.unitPrice.toFixed(2) + ' p/kWh (' + Math.round(this.unitPrice * meta.kwhPerUnit) + ' ' + meta.perUnitLabel + ')';
                }
                return this.unitPrice.toFixed(2) + ' p/kWh';
            },
            get profileDescription() {
                return this.profiles.find(p => p.key === this.profileKey)?.description ?? '';
            },
            get tariffHint() {
                const a = this.r.assumptions;
                const tail = a.effectiveTariffPencePerKwh !== a.baseTariffPencePerKwh
                    ? ' Average paid after battery shifting: ' + a.effectiveTariffPencePerKwh.toFixed(1) + ' p/kWh.'
                    : '';
                if (this.tariffKey === 'typical') {
                    return 'A typical smart heat pump tariff: ' + this.tariffRate.toFixed(1) + ' p/kWh blended, with a '
                        + (TYPICAL_HP_OFFPEAK ?? 0).toFixed(1) + 'p off peak window a battery can use. Rates are editable.' + tail;
                }
                if (this.tariffKey === 'cap') return 'Flat rate with no off peak window.' + tail;
                return 'Custom rate.' + tail;
            },

            // ---- simulation (memoised) ----
            get r() {
                const amount = Math.min(Math.max(Number(this.amount) || 0, 0), this.amountBounds.max);
                const input = {
                    current: {
                        fuel: this.fuel,
                        unit: this.unit,
                        unitPricePencePerKwh: this.unitPrice,
                        boilerEfficiency: this.boilerEff,
                        ...(this.unit === 'kwh' ? { annualKwh: amount }
                            : this.unit === 'bill' ? { annualBillGbp: amount }
                            : { annualFuelUnits: amount }),
                    },
                    heatPump: {
                        spaceScop: this.spaceScop,
                        hotWaterScop: this.hotWaterScop,
                        comfortUpliftC: this.comfortUpliftC > 0 ? this.comfortUpliftC : undefined,
                        removeGasStandingCharge: this.removeGas && this.fuel === 'mains_gas',
                        ...(this.hwMethod === 'occupancy'
                            ? { occupants: this.occupants }
                            : { cylinderLitres: this.cylinderLitres, cylinderTargetC: this.cylinderTargetC, reheatsPerDay: this.reheatsPerDay }),
                    },
                    tariff: {
                        preset: 'custom',
                        electricityPencePerKwh: this.tariffRate,
                        ...(this.offPeakRate != null ? { offPeakPencePerKwh: this.offPeakRate } : {}),
                    },
                    solar: this.solarKwp > 0 ? { systemSizeKwp: this.solarKwp, orientation: this.orientation } : undefined,
                    battery: this.batteryKwh > 0 ? { capacityKwh: this.batteryKwh } : undefined,
                    projection: { years: 10, gasInflationPct: this.gasInfl, electricityInflationPct: this.elecInfl },
                };
                const profile = MONTHLY_PROFILE_PRESETS.find(p => p.key === this.profileKey) ?? MONTHLY_PROFILE_PRESETS[0];
                const key = JSON.stringify([input, profile.key]);
                if (key !== this._resultKey) {
                    this._resultKey = key;
                    this._result = simulate(input, {
                        profiles: {
                            monthlySpaceHeatFraction: profile.monthlySpaceHeatFraction,
                            monthlyCop: profile.monthlyCop,
                        },
                    });
                }
                return this._result;
            },

            get periodDivisor() { return this.period === 'month' ? 12 : 1; },
            get periodLabel() { return this.period === 'month' ? 'a month' : 'a year'; },

            // ---- charts ----
            get monthlyBars() {
                const months = this.r.months;
                const max = Math.max(...months.map(m => Math.max(m.currentCostGbp, m.heatPumpCostGbp)), 1);
                return months.map(m => {
                    const curH = (m.currentCostGbp / max) * 100;
                    const hpH = (m.heatPumpCostGbp / max) * 100;
                    return {
                        label: m.month.slice(0, 1),
                        curH: curH.toFixed(1),
                        hpH: hpH.toFixed(1),
                        curSpacePct: m.currentCostGbp > 0 ? ((m.currentSpaceCostGbp / m.currentCostGbp) * 100).toFixed(1) : 0,
                        curWaterPct: m.currentCostGbp > 0 ? ((m.currentWaterCostGbp / m.currentCostGbp) * 100).toFixed(1) : 0,
                        hpSpacePct: m.heatPumpCostGbp > 0 ? ((m.heatPumpSpaceCostGbp / m.heatPumpCostGbp) * 100).toFixed(1) : 0,
                        hpWaterPct: m.heatPumpCostGbp > 0 ? ((m.heatPumpWaterCostGbp / m.heatPumpCostGbp) * 100).toFixed(1) : 0,
                    };
                });
            },
            get tenYear() {
                const years = this.r.years;
                const max = Math.max(...years.map(y => Math.max(y.currentCostGbp, y.heatPumpCostGbp)), 1);
                const x = i => 4 + (i * 94) / Math.max(years.length - 1, 1);
                const y = v => 38 - (v / max) * 32;
                return {
                    currentPoints: years.map((yr, i) => `${x(i).toFixed(1)},${y(yr.currentCostGbp).toFixed(1)}`).join(' '),
                    hpPoints: years.map((yr, i) => `${x(i).toFixed(1)},${y(yr.heatPumpCostGbp).toFixed(1)}`).join(' '),
                    currentY10: years[years.length - 1]?.currentCostGbp ?? 0,
                    hpY10: years[years.length - 1]?.heatPumpCostGbp ?? 0,
                };
            },

            // ---- show the maths ----
            get mathRows() {
                const a = this.r.assumptions;
                const r = this.r;
                const electric = this.fuel === 'direct_electric' || this.fuel === 'storage_electric';
                const rows = [];
                const hasStanding = this.fuel === 'mains_gas' || this.fuel === 'direct_electric' || this.fuel === 'storage_electric';
                rows.push({ group: '1. What you use today', label: 'Fuel bought', value: this.kwhFmt(a.currentAnnualFuelKwh), how: this.unit === 'bill' ? (hasStanding ? 'Your bill, minus the standing charge, divided by your unit price' : 'Your bill divided by your unit price') : this.unit === 'fuel_units' ? 'Your fuel units x energy per unit' : 'The figure you entered' });
                rows.push({ group: '1. What you use today', label: 'Appliance efficiency', value: Math.round(a.boilerEfficiency * 100) + '%', how: electric ? 'Panel and storage heaters deliver every kWh of electricity as heat' : 'Boiler about ' + this.boilerAge + ' years old, anchored to the EST in situ study of condensing boilers' });
                rows.push({ group: '1. What you use today', label: 'Useful heat', value: this.kwhFmt(a.usefulHeatKwh), how: '= fuel bought x efficiency' });
                rows.push({ group: '2. The heat your home needs', label: 'Hot water', value: this.kwhFmt(a.hotWaterDemandKwh), how: this.hwMethod === 'occupancy' ? this.occupants + ' people x 3 kWh each per day x 365 days' : this.cylinderLitres + ' L cylinder heated to ' + this.cylinderTargetC + ' C, ' + this.reheatsPerDay + ' time(s) a day' });
                rows.push({ group: '2. The heat your home needs', label: 'Space heating', value: this.kwhFmt(a.spaceHeatDemandKwh), how: '= useful heat minus hot water' });
                if (this.comfortUpliftC > 0) {
                    rows.push({ group: '2. The heat your home needs', label: 'Extra warmth', value: '+' + Math.round(this.comfortUpliftC * 8) + '%', how: 'Heating ' + this.comfortUpliftC + ' C warmer with the heat pump adds 8% per degree' });
                }
                rows.push({ group: '3. Heat pump electricity', label: 'Space heating SPF', value: a.spaceScopUsed.toFixed(2), how: 'At ' + this.flowTempC + ' C flow temperature. Efficiency varies by month: worse in winter, better in summer' });
                rows.push({ group: '3. Heat pump electricity', label: 'Hot water SPF', value: a.hotWaterScopUsed.toFixed(2), how: 'The yearly average: a little worse in winter when mains water arrives colder, better in summer. See the monthly numbers under the chart' });
                rows.push({ group: '3. Heat pump electricity', label: 'Electricity needed', value: this.kwhFmt(a.heatPumpAnnualElecKwh), how: "= each month's heat divided by that month's efficiency, added up" });
                // One clear energy balance: what the heat pump needs, then every
                // source it comes from with its rate and yearly cost.
                const SOURCES = '4. Where that electricity comes from';
                const panelsDirectKwh = a.solarToHeatPumpKwh - a.batterySolarStorageKwh;
                const gridStandardKwh = a.heatPumpAnnualElecKwh - a.solarToHeatPumpKwh - a.batteryShiftedKwh;
                if (a.solarAnnualGenerationKwh > 0) {
                    rows.push({ group: SOURCES, label: 'Solar generated', value: this.kwhFmt(a.solarAnnualGenerationKwh), how: this.solarKwp.toFixed(1) + ' kWp x ' + (a.solarYieldKwhPerKwp ?? 0) + ' kWh per kWp. The monthly table above shows when' });
                }
                rows.push({ group: SOURCES, label: 'The heat pump needs', value: this.kwhFmt(a.heatPumpAnnualElecKwh), how: 'Carried down from step 3. Every line below shows where it comes from' });
                if (panelsDirectKwh > 0.5) {
                    rows.push({ group: SOURCES, label: 'From your solar panels', value: this.kwhFmt(panelsDirectKwh), how: 'At 0p, the sun is free: ' + Math.round((a.solarSelfConsumptionFraction ?? 0) * 100) + '% of generation reaches the heat pump, capped by what it needs each month' });
                }
                if (a.batterySolarStorageKwh > 0.5) {
                    rows.push({ group: SOURCES, label: 'From solar stored in the battery', value: this.kwhFmt(a.batterySolarStorageKwh), how: 'At 0p: midday sun the battery holds for morning and evening heating' });
                }
                if (a.batteryShiftedKwh > 0.5 && a.batteryShiftedRatePencePerKwh != null) {
                    rows.push({ group: SOURCES, label: 'From the grid, cheap window via battery', value: this.kwhFmt(a.batteryShiftedKwh), how: 'At ' + a.batteryShiftedRatePencePerKwh.toFixed(1) + 'p (the ' + (a.batteryOffPeakPencePerKwh ?? 0).toFixed(1) + 'p window rate plus battery losses) = ' + this.money((a.batteryShiftedKwh * a.batteryShiftedRatePencePerKwh) / 100) + ' a year' });
                }
                rows.push({ group: SOURCES, label: 'From the grid at your tariff', value: this.kwhFmt(gridStandardKwh), how: 'At ' + a.baseTariffPencePerKwh.toFixed(1) + 'p = ' + this.money((gridStandardKwh * a.baseTariffPencePerKwh) / 100) + ' a year' });
                rows.push({ group: '5. Prices', label: 'Your fuel unit price', value: a.currentUnitPricePencePerKwh.toFixed(2) + ' p/kWh', how: 'Your setting (default ' + this.priceMeta.defaultRate.toFixed(2) + ', ' + this.priceMeta.source + ')' });
                rows.push({ group: '6. The result', label: 'Current yearly cost', value: this.money(r.year1.currentCostGbp), how: '= fuel bought x fuel unit price' });
                rows.push({ group: '6. The result', label: 'Heat pump yearly cost', value: this.money(r.year1.heatPumpCostGbp), how: a.batteryShiftedKwh > 0.5 ? '= the two grid lines in step 4 added up; the solar lines cost nothing' : a.solarToHeatPumpKwh > 0.5 ? '= the grid line in step 4; the solar lines cost nothing' : '= the grid line in step 4' });
                if (a.solarSavingGbp > 0) rows.push({ group: '6. The result', label: 'of which panels alone save', value: this.money(a.solarSavingGbp), how: '= solar the panels deliver by themselves x electricity rate' });
                if (a.batterySavingGbp > 0) {
                    const parts = [];
                    if (a.batterySolarSavingGbp > 0) parts.push('storing solar ' + this.money(a.batterySolarSavingGbp));
                    if (a.batteryArbitrageSavingGbp > 0) parts.push('cheap window shifting ' + this.money(a.batteryArbitrageSavingGbp));
                    rows.push({ group: '6. The result', label: 'of which the battery saves', value: this.money(a.batterySavingGbp), how: '= ' + parts.join(' plus ') });
                }
                if (a.gasStandingChargeSavingGbp > 0) rows.push({ group: '6. The result', label: 'Standing charge saved', value: this.money(a.gasStandingChargeSavingGbp) + ' a year', how: 'Removing the gas connection' });
                rows.push({ group: '6. The result', label: r.year1.savingGbp >= 0 ? 'Estimated saving' : 'Estimated extra cost', value: this.money(Math.abs(r.year1.savingGbp)) + ' a year', how: '= current cost minus heat pump cost' + (a.gasStandingChargeSavingGbp > 0 ? ' plus standing charge saved' : ''), total: true });
                return rows;
            },

            get displayRows() {
                const rows = [];
                let lastGroup = null;
                for (const row of this.mathRows) {
                    if (row.group !== lastGroup) {
                        rows.push({ type: 'group', label: row.group });
                        lastGroup = row.group;
                    }
                    rows.push({ type: 'row', ...row });
                }
                return rows;
            },

            // ---- helpers ----
            money(value) {
                const rounded = Math.round(value);
                return '£' + rounded.toLocaleString('en-GB');
            },
            kwhFmt(value) {
                return Math.round(value).toLocaleString('en-GB') + ' kWh a year';
            },
            round2(value) {
                return Math.round(value * 100) / 100;
            },
            scopForFlow(flowTempC) {
                return spaceScopForFlowTemp(flowTempC);
            },
            effForAge(fuel, ageYears) {
                return boilerEfficiencyForAge(fuel, ageYears);
            },
        };
        return state;
    };

    const injectStyles = () => {
        if (document.getElementById('hpcs-styles')) return;
        const style = document.createElement('style');
        style.id = 'hpcs-styles';
        style.textContent = STYLES;
        document.head.appendChild(style);
    };

    const mount = root => {
        if (!root || root.dataset.hpcsMounted === 'true') return;
        if (!window.Alpine) return;
        root.dataset.hpcsMounted = 'true';
        injectStyles();
        root.innerHTML = TEMPLATE;
        root.firstElementChild?.setAttribute('x-data', 'heatPumpCostSimulator()');
        // Alpine initialises the injected subtree itself: via its initial
        // document walk when it starts after this runs, or via its mutation
        // observer when it is already running. Calling initTree here as well
        // double-initialises the tree (Alpine 3.14.1 has no dedupe guard),
        // and Alpine.data components auto-invoke init(), so no x-init either.
    };

    const findRoot = () => document.querySelector('[data-journey="heat-pump-cost-simulator"]');

    const register = () => {
        if (!window.Alpine || typeof window.Alpine.data !== 'function') return;
        window.Alpine.data('heatPumpCostSimulator', createStore);
        mount(findRoot());
    };

    if (window.Alpine) {
        register();
    } else {
        document.addEventListener('alpine:init', register);
    }

    const ensureMounted = () => {
        const root = findRoot();
        if (root) {
            if (window.Alpine) mount(root);
            return;
        }
        const observer = new MutationObserver(() => {
            const nextRoot = findRoot();
            if (!nextRoot) return;
            if (window.Alpine) mount(nextRoot);
            observer.disconnect();
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 5000);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', ensureMounted);
    } else {
        ensureMounted();
    }
}

registerHeatPumpCostSimulator();
